import { ApiError, apiRequest } from './client'

const API_BASE = (import.meta.env.VITE_API_URL ?? '').replace(/\/+$/, '')

function buildUrl(path: string): string {
  const normalized = path.startsWith('/') ? path : `/${path}`
  return `${API_BASE}${normalized}`
}

export interface CapacityRequestDoc {
  id: string
  filename: string
  mimeType: string
  byteSize: number
  uploadedAt: string
}

export interface CapacityRequest {
  id: string
  teamId: string
  teamName: string
  requestedById: string | null
  requestedByName: string | null
  reason: string
  previousSlotPct: number | null
  newSlotPct: number | null
  previousFloor: number | null
  newFloor: number | null
  effectiveFrom: string | null
  effectiveTo: string | null
  isPermanent: boolean
  recordedById: string
  recordedByName: string | null
  createdAt: string
  documents: CapacityRequestDoc[]
}

export interface CreateCapacityRequestInput {
  teamId: string
  requestedById?: string | null
  reason: string
  newSlotPct?: number | null
  newFloor?: number | null
  effectiveFrom?: string | null
  effectiveTo?: string | null
}

/** History of recorded capacity changes, newest first. */
export function listCapacityRequests(teamId?: string): Promise<CapacityRequest[]> {
  const qs = new URLSearchParams({ limit: '50' })
  if (teamId) qs.set('teamId', teamId)
  return apiRequest<CapacityRequest[]>(`/api/v1/hr/capacity-requests?${qs.toString()}`)
}

/** Record an offline-approved capacity change (applies it to the team). */
export function createCapacityRequest(
  input: CreateCapacityRequestInput,
): Promise<CapacityRequest> {
  return apiRequest<CapacityRequest>('/api/v1/hr/capacity-requests', {
    method: 'POST',
    body: input,
  })
}

/** Attach a supporting document to a recorded change (multipart). */
export async function uploadCapacityRequestDocument(
  requestId: string,
  file: File,
): Promise<CapacityRequestDoc> {
  const form = new FormData()
  form.append('file', file)
  const response = await fetch(
    buildUrl(`/api/v1/hr/capacity-requests/${encodeURIComponent(requestId)}/documents`),
    { method: 'POST', credentials: 'include', body: form },
  )
  const text = await response.text()
  const payload: unknown = text.length > 0 ? JSON.parse(text) : null
  if (!response.ok) {
    const message =
      typeof payload === 'object' && payload !== null && 'error' in payload
        ? String((payload as { error: unknown }).error)
        : `Upload failed (${response.status})`
    throw new ApiError(message, response.status, payload)
  }
  return (payload as { data: CapacityRequestDoc }).data
}

/** Streaming URL for a document (served through the api pod). */
export function capacityRequestDocUrl(requestId: string, docId: string): string {
  return buildUrl(
    `/api/v1/hr/capacity-requests/${encodeURIComponent(requestId)}/documents/${encodeURIComponent(docId)}`,
  )
}
